import {
  Header,
  type IHeaderProps,
  Space,
  Spacer,
  SubText,
} from '@procurify/ui'

import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { FormattedTime } from '@webapp/core/components/FormattedTime'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  AuditLogRecordChange,
  isEmptyChange,
} from '@webapp/platform/audit-log/components/AuditLogRecordChange'
import { AuditLogRecordLabel } from '@webapp/platform/audit-log/components/AuditLogRecordLabel'
import { type AuditLogChangedField } from '@webapp/platform/audit-log/entities/AuditLogChangedField'
import { type AuditLogStatusEnum } from '@webapp/platform/audit-log/entities/enums/AuditLogStatusEnum'
import messages from './messages'
import { AuditLogRecordStyled } from './styles/AuditLogRecordStyled'

export interface IAuditLogRecordProps {
  status: AuditLogStatusEnum
  title: string
  userName: string
  changedOn: Date
  changedFields: AuditLogChangedField[]
  delegateeUserName?: string
  /**
   * Level of the record title, one below the heading the host puts over the
   * log. Defaults to `h5`, under an h4 section heading.
   */
  headingLevel?: IHeaderProps['as']
}

export const AuditLogRecord = ({
  status,
  title,
  userName,
  changedOn,
  changedFields,
  delegateeUserName,
  headingLevel = 'h5',
}: IAuditLogRecordProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()
  const recordedChanges =
    changedFields?.filter((change) => !isEmptyChange(change)) ?? []

  return (
    <AuditLogRecordStyled status={status}>
      <AuditLogRecordLabel status={status} />

      <Space size='xsmall'>
        <Header as={headingLevel}>{title}</Header>
        <SubText inline color='textSecondary'>
          {delegateeUserName ? (
            intl.formatMessage(messages.delegateeDetail, {
              delegateeUserName,
              userName,
            })
          ) : (
            <strong>{userName}</strong>
          )}
          {' | '}
          <SubText inline bold color='textSecondary'>
            {formatDate(changedOn, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}{' '}
            <FormattedTime date={changedOn} />
          </SubText>
        </SubText>
      </Space>

      {recordedChanges.length > 0 && (
        <>
          <Spacer />

          <Space size='small'>
            {recordedChanges.map((record) => (
              <AuditLogRecordChange key={record.field} {...record} />
            ))}
          </Space>
        </>
      )}
    </AuditLogRecordStyled>
  )
}
